import { MouseEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ListItemIcon, ListItemText, Menu, MenuItem, SvgIcon, Tooltip } from '@mui/material';

import { AppButton, AppButtonCollapsedContext } from '@/shared/ui/AppButton';

import { ButtonGroup, getToolbarAppButtonSx } from './GeneratikTableToolbar.styles';
import { ToolbarActionButton, ToolbarActionMenuItem } from './GeneratikTableToolbar.types';

const menuSlotProps = {
  paper: {
    sx: {
      mt: '0.25rem',
      borderRadius: '0.75rem',
      background: '#FEFEFE',
      boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.15)',
      '& .MuiList-root': {
        p: '0.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.125rem',
      },
    },
  },
};

const renderMenuItem = (item: ToolbarActionMenuItem, onSelect: () => void) => (
  <MenuItem
    key={item.id}
    onClick={onSelect}
    disabled={item.disabled}
    data-testid={item.dataTestId}
    sx={{
      display: 'flex',
      gap: '0.25rem',
      p: '0.25rem 0.5rem',
      minHeight: 'unset',
      alignItems: 'center',
      borderRadius: '0.5rem',
    }}
  >
    {item.icon && (
      <ListItemIcon sx={{ minWidth: 'unset !important', color: '#737085' }}>
        {item.icon}
      </ListItemIcon>
    )}
    <ListItemText
      primary={item.label}
      primaryTypographyProps={{
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.6,
        color: '#737085',
      }}
    />
  </MenuItem>
);

interface ToolbarLeftSectionProps {
  buttons: ToolbarActionButton[] | ReactNode;
  collapsed: boolean;
}

const ToolbarLeftSection = ({ buttons, collapsed }: ToolbarLeftSectionProps) => {
  const { t } = useTranslation();
  const [moreActionsAnchor, setMoreActionsAnchor] = useState<HTMLElement | null>(null);
  const [actionMenu, setActionMenu] = useState<{
    buttonId: string;
    anchor: HTMLElement;
  } | null>(null);

  if (!Array.isArray(buttons)) {
    return (
      <AppButtonCollapsedContext.Provider value={collapsed}>
        {buttons}
      </AppButtonCollapsedContext.Provider>
    );
  }

  if (buttons.length === 0) return null;

  const inlineButtons = buttons.filter((button) => !button.overflow);
  const overflowButtons = buttons.filter((button) => button.overflow);
  const openActionMenuButton = actionMenu
    ? inlineButtons.find((button) => button.id === actionMenu.buttonId)
    : undefined;

  return (
    <ButtonGroup>
      {inlineButtons.map((button) => {
        const tooltipText = button.tooltip || (collapsed ? button.label : '');
        return (
          <Tooltip key={button.id} title={tooltipText} arrow disableHoverListener={!tooltipText}>
            <span>
              <AppButton
                variant={
                  button.color === 'error'
                    ? 's-alert'
                    : button.color === 'success'
                      ? 's-success'
                      : 'secondary'
                }
                size="small"
                collapsed={collapsed}
                startIcon={button.icon}
                onClick={
                  button.menuItems
                    ? (event: MouseEvent<HTMLElement>) =>
                        setActionMenu({ buttonId: button.id, anchor: event.currentTarget })
                    : button.onClick
                }
                disabled={button.disabled}
                loading={button.loading}
                data-testid={button.dataTestId}
                sx={getToolbarAppButtonSx(collapsed)}
              >
                {button.label}
              </AppButton>
            </span>
          </Tooltip>
        );
      })}

      {openActionMenuButton?.menuItems && (
        <Menu
          anchorEl={actionMenu?.anchor}
          open
          onClose={() => setActionMenu(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={menuSlotProps}
        >
          {openActionMenuButton.menuItems.map((item) =>
            renderMenuItem(item, () => {
              setActionMenu(null);
              item.onClick?.();
            }),
          )}
        </Menu>
      )}

      {overflowButtons.length > 0 && (
        <>
          <AppButton
            variant="secondary"
            size="small"
            collapsed={collapsed}
            startIcon={
              <SvgIcon viewBox="0 0 16 16" sx={{ width: '1rem', height: '1rem' }}>
                <path
                  d="M9.33215 9.4998C9.60829 9.4998 9.83215 9.72366 9.83215 9.9998C9.83215 10.2759 9.60829 10.4998 9.33215 10.4998H6.66548C6.38934 10.4998 6.16548 10.2759 6.16548 9.9998C6.16548 9.72366 6.38934 9.4998 6.66548 9.4998H9.33215Z"
                  fill="currentColor"
                />
                <path
                  d="M5.12864 1.53864C5.48487 1.46513 5.85501 1.49954 6.19179 1.63695C6.52323 1.77221 6.80761 2.00158 7.01015 2.29645L7.572 3.07249L8.47044 2.89281C8.84012 2.80199 9.22939 2.82784 9.58372 2.96833C9.94708 3.11246 10.2547 3.36931 10.462 3.70075C10.6692 4.03215 10.7655 4.42103 10.7361 4.81077C10.7067 5.19874 10.5538 5.56708 10.3012 5.86286L9.85325 6.39671L10.2921 7.05166C10.4456 7.281 10.3845 7.5913 10.1553 7.74502C9.92594 7.89863 9.61501 7.8376 9.46132 7.6083L8.81419 6.6415C8.6904 6.45666 8.70372 6.2123 8.84674 6.04189L9.53554 5.22028L9.53945 5.21572C9.65523 5.08102 9.72519 4.91301 9.73866 4.7359C9.75208 4.55869 9.70855 4.38138 9.61432 4.2307C9.52008 4.08003 9.37977 3.96351 9.21458 3.89801C9.04942 3.83258 8.86751 3.82162 8.6957 3.86676C8.68622 3.86925 8.67665 3.87135 8.66705 3.87327L7.45351 4.11611C7.26172 4.15446 7.06495 4.07728 6.95025 3.91885L6.20025 2.88304C6.19692 2.87844 6.19366 2.87343 6.19049 2.86872C6.09818 2.73165 5.96719 2.62517 5.81419 2.56273C5.66116 2.50028 5.49299 2.48446 5.33111 2.51781C5.16917 2.55122 5.02054 2.63225 4.90468 2.75023C4.79273 2.86423 4.71653 3.0081 4.68398 3.16429L4.59153 4.40387C4.57534 4.61934 4.42239 4.79999 4.21262 4.85179L3.08372 5.12979C2.92226 5.18039 2.78028 5.27951 2.67616 5.41299C2.57095 5.54796 2.50974 5.71213 2.50103 5.88304C2.49239 6.05399 2.53629 6.22377 2.62734 6.36872C2.71845 6.51369 2.85231 6.6272 3.01015 6.69359L3.44439 6.87197C3.69966 6.97697 3.82137 7.26899 3.71653 7.52432C3.61158 7.77969 3.31957 7.90197 3.06419 7.7971L2.62603 7.61676L2.62213 7.61546C2.27494 7.46941 1.9814 7.21953 1.78098 6.90062C1.58062 6.58173 1.48333 6.20904 1.50234 5.83291C1.52137 5.45674 1.65556 5.09547 1.8871 4.7984C2.1187 4.50131 2.4364 4.28236 2.79661 4.1721L2.82265 4.16494L3.62083 3.96768L3.69049 3.05101L3.69765 2.99632C3.7647 2.63881 3.93629 2.30925 4.19114 2.04971C4.446 1.79018 4.77241 1.61218 5.12864 1.53864Z"
                  fill="currentColor"
                />
                <path
                  d="M10.6108 1.47729C10.8894 1.36135 11.2021 1.35776 11.4832 1.46752L13.864 2.39851L13.8647 2.39916C14.0094 2.45591 14.1414 2.54075 14.2527 2.64916C14.364 2.75761 14.4526 2.88754 14.5131 3.03067C14.5737 3.17394 14.6054 3.32827 14.6056 3.4838C14.6057 3.63929 14.5746 3.79354 14.5144 3.93692L13.0079 7.52351C12.901 7.77809 12.6076 7.89804 12.353 7.79109C12.0984 7.68412 11.9791 7.39071 12.086 7.13614L13.5926 3.54955L13.6023 3.51765C13.6044 3.50697 13.6056 3.49605 13.6056 3.4851C13.6055 3.46288 13.6012 3.44046 13.5926 3.42C13.5839 3.39958 13.5707 3.38144 13.5548 3.36596C13.5389 3.35047 13.5201 3.33826 13.4995 3.33015L11.1199 2.39851C11.0797 2.38282 11.0347 2.3839 10.9949 2.40047C10.9552 2.41703 10.9235 2.44823 10.9064 2.48771L10.9057 2.48836L10.2898 3.90307C10.1795 4.15604 9.88469 4.27173 9.63161 4.16153C9.37859 4.05121 9.26294 3.75644 9.37315 3.50333L9.98904 2.08862C10.1094 1.81207 10.3323 1.59318 10.6108 1.47729Z"
                  fill="currentColor"
                />
                <path
                  d="M12.8321 7.9998C12.8321 7.95559 12.8146 7.91321 12.7833 7.88196C12.7521 7.85071 12.7097 7.83313 12.6655 7.83313H3.33084L3.29894 7.83639C3.28824 7.83856 3.2778 7.84191 3.26769 7.84615C3.24754 7.85464 3.22902 7.86707 3.21365 7.88261C3.19833 7.89811 3.18609 7.91648 3.17784 7.93665C3.16959 7.95692 3.16534 7.97921 3.16547 8.0011V12.6665C3.16547 12.8875 3.25334 13.0994 3.40961 13.2557C3.56589 13.4119 3.7778 13.4998 3.99881 13.4998H11.9988C12.2198 13.4998 12.4317 13.4119 12.588 13.2557C12.7443 13.0994 12.8321 12.8875 12.8321 12.6665V7.9998ZM13.8321 12.6665C13.8321 13.1527 13.6388 13.6189 13.295 13.9627C12.9512 14.3065 12.485 14.4998 11.9988 14.4998H3.99881C3.51258 14.4998 3.0464 14.3065 2.70258 13.9627C2.35877 13.6189 2.16547 13.1527 2.16547 12.6665V8.0037C2.16496 7.85155 2.19402 7.70064 2.25141 7.55969C2.30919 7.4178 2.39438 7.28847 2.50206 7.17949C2.60966 7.0706 2.73796 6.98439 2.87901 6.92493C3.02015 6.86547 3.17183 6.83407 3.32498 6.83313H12.6655C12.9749 6.83313 13.2716 6.95614 13.4903 7.17493C13.7091 7.39372 13.8321 7.69038 13.8321 7.9998V12.6665Z"
                  fill="currentColor"
                />
              </SvgIcon>
            }
            onClick={(e) => setMoreActionsAnchor(e.currentTarget)}
            disabled={overflowButtons.every((button) => button.disabled)}
            data-testid="more-actions-button"
            sx={getToolbarAppButtonSx(collapsed)}
          >
            {t('More Actions')}
          </AppButton>
          <Menu
            anchorEl={moreActionsAnchor}
            open={Boolean(moreActionsAnchor)}
            onClose={() => setMoreActionsAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={menuSlotProps}
          >
            {overflowButtons.map((button) =>
              renderMenuItem(button, () => {
                setMoreActionsAnchor(null);
                button.onClick?.();
              }),
            )}
          </Menu>
        </>
      )}
    </ButtonGroup>
  );
};

export default ToolbarLeftSection;
